
import classes from "./GridTable.module.scss";
import { forwardRef, useDeferredValue, useEffect, useImperativeHandle, useState, useMemo, useCallback } from "react";
import { calculateGridSize, calculateIndexPositions, initializeLetterState } from "../../../utils/table";
import { WordControl } from "../WordControl";
import { useRef } from "react";
import { getCorrectWords, getIncorrectWords } from "../../../utils/correction";
import { useDispatch, useSelector } from "react-redux";
import { finishGame, goToQuestion, setShowBankControl } from "@educaplay/store/slices/gameSlice";
import { clone, Fill, useGameSounds, useMediaQuery, useTranslate } from "@educaplay/core";
import { getNextID } from "../../../utils/focus";
import { useAnimate, stagger, motion } from "framer-motion";
import { CRUCIGRAMA_SOUNDS } from "../../../utils/constants";
import SkipButton from "../skipButton";
import { ExitIcon, SkipIcon } from "../Icons";
import clsx from "clsx";
import { InteractiveBox } from "../InteractiveBox";

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const GridTable = forwardRef(function GridTable(props, ref) {
    const { onAnswer, questions, numberScreens } = props;
    const [currentScreen, setCurrentScreen] = useState(0);
    const dispatch = useDispatch();

    const currentQuestionsScreen = useMemo(() =>
        questions.filter(question => question.screenID === currentScreen),
        [questions, currentScreen]
    );

    const [gridSize, setGridSize] = useState(() => calculateGridSize(currentQuestionsScreen));
    const [words, setWords] = useState([]);
    const [indexPositions, setIndexPositions] = useState([]);
    const [letterState, setLetterState] = useState([]);
    const wordRefs = useRef({});
    const [wordsAnsweredCorrect, setWordsAnsweredCorrect] = useState([]);
    const [wordsAnsweredFailed, setWordsAnsweredFailed] = useState([]);
    const isInputBlockedRef = useRef(false);
    const [currentFocus, setCurrentFocus] = useState(null);
    const deferredCurrentFocus = useDeferredValue(currentFocus);
    const [tableRef, animate] = useAnimate();
    const t = useTranslate();
    const lives = useSelector(state => state.game.liveActived ? state.game.lives : null);
    const gameSounds = useGameSounds();
    const currentWordFocus = deferredCurrentFocus ? deferredCurrentFocus.wordId : null;
    const showBankControl = useSelector(state => state.game.showBankControl);
    const isDesktop = useMediaQuery("(min-width: 1024px)");
    const hasMoreScreens = currentScreen < numberScreens - 1;
    const isTimeoutExceededRef = useRef(false);
    const timeoutRef = useRef(null);
    const InteractiveBoxRef = useRef(null);

    useEffect(() => {
        if (currentScreen !== 0) animate(tableRef.current, { opacity: 0 });
        setGridSize(calculateGridSize(currentQuestionsScreen))
        setWords(currentQuestionsScreen);
        setIndexPositions(calculateIndexPositions(currentQuestionsScreen));
        setLetterState(initializeLetterState(currentQuestionsScreen));
        setWordsAnsweredCorrect([]);
        setWordsAnsweredFailed([]);
        isInputBlockedRef.current = false;
        setCurrentFocus(null);
        isTimeoutExceededRef.current = false;
        dispatch(goToQuestion(currentQuestionsScreen[0].id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentScreen]);

    useEffect(() => {
        if (currentWordFocus === null) {
            gameSounds.play(CRUCIGRAMA_SOUNDS.MOVE);
            dispatch(setShowBankControl(false));
            return;
        }
        gameSounds.play(CRUCIGRAMA_SOUNDS.GOOD_ACTIVE);
        if (!showBankControl) dispatch(setShowBankControl(true));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentWordFocus, gameSounds, dispatch])

    useImperativeHandle(ref, () => ({
        animateEntry: async () => {
            await animate([[tableRef.current, { opacity: 1 }, { duration: 0.3 }]]);
            await animate([["[data-animate-id='cell']", { opacity: 1 }, { delay: stagger(0.03), duration: 0.2 }]]);
            InteractiveBoxRef.current.animateEntry();

            const firstWord = words[0];
            const firstLetter = firstWord.lettersPositions[0];

            wordRefs.current[firstWord.id].focus(firstLetter.id);
        },
        animateExit: async () => {
            await animate([[tableRef.current, { opacity: 0 }, { duration: 0.3 }]]);
        }
    }), [animate, words, tableRef]);

    const onChangeHandler = async (newLetterState) => {
        if (isInputBlockedRef.current) return false;
        isInputBlockedRef.current = true;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        isTimeoutExceededRef.current = false;
        timeoutRef.current = setTimeout(() => {
            isTimeoutExceededRef.current = true;
        }, 10000);

        const initialCorrectWords = getCorrectWords(newLetterState, words);
        const correctWords = initialCorrectWords.filter(word => !wordsAnsweredCorrect.includes(word.id));
        const initialIncorrectWords = lives ? getIncorrectWords(newLetterState, words) : [];
        const incorrectWords = initialIncorrectWords.filter(word => !wordsAnsweredFailed.includes(word.id));

        const calculateState = () => {
            const clonedState = clone(newLetterState);
            for (const correctWord of correctWords) {
                correctWord.lettersPositions.forEach(letter => {
                    clonedState[letter.id].correct = true;
                })
            }
            for (const incorrectWord of incorrectWords) {
                incorrectWord.lettersPositions.forEach(letter => {
                    if (clonedState[letter.id].correct === true) return;
                    clonedState[letter.id].correct = false;
                })
            }
            return clonedState;
        }

        const state = calculateState();
        setLetterState(state);

        const uniqueCorrectWords = [...new Set([...correctWords.map(word => word.id), ...wordsAnsweredCorrect])];
        if (wordsAnsweredCorrect.length !== uniqueCorrectWords.length) setWordsAnsweredCorrect(uniqueCorrectWords);

        const uniqueIncorrectWords = [...new Set([...incorrectWords.map(word => word.id), ...wordsAnsweredFailed])];
        if (wordsAnsweredFailed.length !== uniqueIncorrectWords.length) setWordsAnsweredFailed(uniqueIncorrectWords);

        //ANIMATIONS

        const sucessColor = getComputedStyle(document.documentElement).getPropertyValue('--game-success-color');
        const errorColor = getComputedStyle(document.documentElement).getPropertyValue('--game-error-color');
        const successTextColor = getComputedStyle(document.documentElement).getPropertyValue('--success-text-color');
        const errorTextColor = getComputedStyle(document.documentElement).getPropertyValue('--error-text-color');
        for (const correctWord of correctWords) {
            gameSounds.play(CRUCIGRAMA_SOUNDS.CORRECT);
            wordRefs.current[correctWord.id].blur();
            await animate([[`[data-animate-wordid="${correctWord.id}"]`, { backgroundColor: sucessColor, color: successTextColor, boxShadow: "0", zIndex: 20 }, { delay: stagger(0.04), duration: 0.2 }]])
            const userAnswer = correctWord.lettersPositions.map(letter => newLetterState[letter.id].value).join("");
            await onAnswer(userAnswer, { nextQuestionIndex: correctWord.id, questionIndexAnswer: correctWord.id });
            await wait(400);
        }

        for (const incorrectWord of incorrectWords) {
            wordRefs.current[incorrectWord.id].blur();
            await animate([[`[data-animate-wordid="${incorrectWord.id}"]`, { backgroundColor: errorColor, color: errorTextColor, boxShadow: "0", zIndex: 20 }, { delay: stagger(0.04), duration: 0.2 }]])
            const userAnswer = incorrectWord.lettersPositions.map(letter => newLetterState[letter.id].value).join("");
            await onAnswer(userAnswer, { nextQuestionIndex: incorrectWord.id, delayLiveAnimation: 300, questionIndexAnswer: incorrectWord.id });
            await wait(400);
        }

        isInputBlockedRef.current = false;

        //FINISH SCREEN
        if (uniqueCorrectWords.length === words.length) {
            if (hasMoreScreens) {
                setCurrentScreen(currentScreen + 1);
                return false;
            }
            dispatch(finishGame());
            return false;
        }

        //CALCULATE NEXT FOCUS  
        const wordId = currentFocus?.wordId ?? null;
        if (wordId === null) return false;
        const hasAccert = correctWords.length > 0;
        const hasFailed = incorrectWords.length > 0;

        if (hasAccert && hasFailed) {
            setTimeout(() => {
                const lettersPositionsReverse = [...incorrectWords[0].lettersPositions].reverse();
                const firstFocusLetter = lettersPositionsReverse.find(letter => !state[letter.id].correct);
                wordRefs.current[incorrectWords[0].id].focus(firstFocusLetter.id);
            },);
            return true;
        }
        if (hasAccert) {
            const nextWord = words.find(word => !uniqueCorrectWords.includes(word.id));
            const firstFocusLetter = nextWord.lettersPositions.find(letter => !state[letter.id].value) ?? nextWord.lettersPositions.find(letter => !state[letter.id].correct);
            wordRefs.current[nextWord.id].focus(firstFocusLetter.id);
            return true;
        }
        if (hasFailed) {
            setTimeout(() => {
                wordRefs.current[wordId].focusEnd();
            });
            return true;
        }

        return false;
    }

    const findNextWordAndFocus = ({ orderedWords, letterState, wordsAnsweredCorrect, wordRefs }) => {
        const nextWord = orderedWords.find(word => !wordsAnsweredCorrect.includes(word.id));
        if (nextWord) {
            const firstFocusLetter = nextWord.lettersPositions.find(letter => !letterState[letter.id].correct);
            if (firstFocusLetter) {
                wordRefs.current[nextWord.id].focus(firstFocusLetter.id);
            }
        }
    };

    const moveFocus = ({ nextID, originalWordID, letterState, wordRefs, words, row, column, direction }) => {
        let nextIDAux = nextID;

        while (letterState[nextIDAux] && letterState[nextIDAux].correct) {
            nextIDAux = getNextID(row, column, direction);
            if (letterState[nextIDAux].correct === true) {
                const word = words.find(word => word.id === originalWordID);
                const nextLetterIndex = word.lettersPositions.findIndex(letter => letter.id === nextIDAux);
                const letters = [...word.lettersPositions.slice(nextLetterIndex + 1), ...word.lettersPositions.slice(0, nextLetterIndex)];
                const orderedLetter = (direction === 'right' || direction === 'down') ? letters : letters.reverse();
                nextIDAux = orderedLetter.find(letter => !letterState[letter.id].correct).id;
                if (!nextIDAux) return;
            }
            if (!letterState[nextIDAux]) return;
        }

        const posibleWords = words.filter(word => word.lettersPositions.find(letter => letter.id === nextIDAux))
            .map(word => word.id);
        const wordID = posibleWords.length > 1 && posibleWords.includes(originalWordID) ? originalWordID : posibleWords[0];

        wordRefs.current[wordID].focus(nextIDAux);
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [currentScreen]);

    const onKeyDownHandler = ({ event, id, originalWordID }) => {
        const [row, column] = id.split("-").map(Number);
        const originalWord = words.find(word => word.id === originalWordID);
        const directions = { ArrowRight: 'right', ArrowLeft: 'left', ArrowUp: 'up', ArrowDown: 'down' };

        const direction = directions[event.key];
        if (!direction) return;

        const nextID = getNextID(row, column, direction);

        if (!letterState[nextID]) {
            if (['right', 'left'].includes(direction) && (originalWord.direction === "E" || originalWord.direction === "O")) {
                const orderedWords = direction === 'right'
                    ? [...words.slice(originalWordID + 1), ...words.slice(0, originalWordID)]
                    : [...words.slice(originalWordID + 1), ...words.slice(0, originalWordID)].reverse();
                findNextWordAndFocus({ orderedWords, letterState, wordsAnsweredCorrect, wordRefs });
            } else if (['up', 'down'].includes(direction) && (originalWord.direction === "N" || originalWord.direction === "S")) {
                const orderedWords = direction === 'down'
                    ? [...words.slice(originalWordID + 1), ...words.slice(0, originalWordID)]
                    : [...words.slice(originalWordID + 1), ...words.slice(0, originalWordID)].reverse();
                findNextWordAndFocus({ orderedWords, letterState, wordsAnsweredCorrect, wordRefs });
            }
            return;
        }

        moveFocus({ nextID, originalWordID, letterState, wordRefs, words, row, column, direction });
    };

    const gridStyle = useMemo(() => ({
        gridTemplateRows: `repeat(${gridSize.rows}, 24px)`,
        gridTemplateColumns: `repeat(${gridSize.columns}, 24px)`,
    }), [gridSize.rows, gridSize.columns]);

    const handleFocusChange = useCallback((focusState) => {
        if (!focusState) {
            setCurrentFocus(null);
            return;
        }
        const { wordId = null, letterId = null } = focusState;
        setCurrentFocus({ wordId, letterId });
    }, []);

    const wordCountText = useMemo(() => {
        const wordText = t("specific.word");
        return `${wordText}: ${wordsAnsweredCorrect.length}/${words.length}`;
    }, [t, wordsAnsweredCorrect.length, words.length]);

    const screenCountText = useMemo(() => {
        const screenText = t("specific.screen");
        return `${screenText}: ${currentScreen + 1}/${numberScreens}`;
    }, [t, currentScreen, numberScreens]);
    return (
        <>
            <Fill name={"core-bank-right"}>
                {wordCountText}
            </Fill>
            <Fill name={"core-bank-left"}>
                {screenCountText}
            </Fill>
            <InteractiveBox ref={InteractiveBoxRef} currentScreen={currentScreen}>
                <motion.div
                    ref={tableRef}
                    className={classes.table}
                    style={gridStyle}
                >
                    {words.map((question) => {
                        return (
                            <WordControl
                                ref={ref => wordRefs.current[question.id] = ref}
                                key={question.id}
                                wordId={question.id}
                                wordDistribution={question.lettersPositions}
                                letterState={letterState}
                                indexPositions={indexPositions}
                                onChange={onChangeHandler}
                                onFocus={handleFocusChange}
                                onKeyDown={onKeyDownHandler}
                                onAnswer={onAnswer}
                            />
                        )
                    })}
                </motion.div>
            </InteractiveBox>
            <div className={classes.skipbuttoncontainer}>
                <SkipButton
                    className={clsx(!hasMoreScreens && classes.disablegap)}
                    showButton={isTimeoutExceededRef.current}
                    msg={hasMoreScreens ? t('specific.modalmsgpass') : t("common.game.abandon")}
                    onComplete={useCallback(() => {
                        isTimeoutExceededRef.current = false;
                        if (hasMoreScreens) {
                            setCurrentScreen(currentScreen + 1);
                            return;
                        }
                        dispatch(finishGame());
                    }, [hasMoreScreens, currentScreen, dispatch])}
                >
                    <span className={clsx(classes.icon, !hasMoreScreens && classes.iconexit)}>
                        {hasMoreScreens ? <SkipIcon /> : <ExitIcon />}
                    </span>
                    {
                        isDesktop && (
                            <span className={classes.message}>{hasMoreScreens ? t("specific.nextScreen") : t("specific.finish")}</span>
                        )
                    }
                </SkipButton>
            </div>
        </>
    );
});
